import * as React from "react";
import classNames from "classnames";
import { useTranslation } from "react-i18next";
import { QuestionModel } from "../../../models/QuestionModel";
import useStyles from "./FlowPanel.styles";
import QuestionTypeIcon from "../shared/QuestionTypeIcon";
import { FontAwesomeIcon } from "../../shared/FontAwesome";
import { QuestionActionType } from "../../../models/enums/QuestionActionType";
import {
    getPropertyEnum,
    getPropertyString,
    getPropertyBoolean,
} from "../../../models/utilities/propertyUtilities";
import { PropertyType } from "../../../models/enums/PropertyType";
import { DispositionCode } from "../../../models/enums/DispositionCode";
import { useThunkDispatch } from "../../../redux/thunks";
import { ActionModel } from "../../../models/ActionModel";
import { getExitUrl } from "../../../models/utilities/actionUtilities";
import { isNullOrEmpty } from "../../../utils";
import ActionTypeIcon from "../shared/ActionTypeAction";
import { evaluate } from "../../../redux/thunks/evaluator";

interface FlowQuestionProps {
    question: QuestionModel;
    firstOfPage: boolean;
    active?: boolean;
    simulated?: boolean;
    completed?: boolean;
    selected?: boolean;
}

export default function FlowQuestion({
    question,
    firstOfPage,
    active,
    simulated,
    selected,
    completed,
}: FlowQuestionProps) {
    const { t } = useTranslation();
    const { name, type, preLoadActions, postAnswerActions } = question;
    const [showLogic, setShowLogic] = React.useState(false);
    const ref = React.useRef<HTMLDivElement>();
    const dispatch = useThunkDispatch();
    const actions = [...preLoadActions, ...postAnswerActions];
    const classes = useStyles({ active, simulated, selected, completed });

    function getActionTitle(action: ActionModel) {
        switch (action.type) {
            case QuestionActionType.ExitSurvey: {
                const disposition = getPropertyEnum(action, DispositionCode, PropertyType.Status);
                return t("debug.panels.flow.exit", { code: DispositionCode[disposition] });
            }
            case QuestionActionType.BranchTo: {
                const destination = getPropertyString(action, PropertyType.Question);
                const autoClean = getPropertyBoolean(action, PropertyType.AutoClean);
                const status = getPropertyEnum(action, DispositionCode, PropertyType.Status);
                const exitUrl = dispatch((d, getState) => getExitUrl(action, getState().interview));
                if (status > 0 && !isNullOrEmpty(exitUrl))
                    return t("debug.panels.flow.branchToExitUrl", {
                        url: exitUrl,
                        code: DispositionCode[status],
                    });
                return t("debug.panels.flow.branchTo", {
                    destination,
                    context: autoClean && "autoclean",
                });
            }
            case QuestionActionType.ComputeVariable: {
                const variable = getPropertyString(action, PropertyType.CalculatedVariableName);
                const value = getPropertyString(action, PropertyType.Value);
                return t("debug.panels.flow.computeVariable", { variable, value });
            }
            case QuestionActionType.SetVariableValue: {
                const variable = getPropertyString(action, PropertyType.Variable);
                const matrix = getPropertyString(action, PropertyType.MatrixIndex);
                const value = getPropertyString(action, PropertyType.Value);
                return t("debug.panels.flow.setVariable", {
                    variable,
                    value,
                    matrix,
                    context: isNullOrEmpty(matrix) && "row",
                });
            }
            case QuestionActionType.Selection:
                return t("debug.panels.flow.selection");
            case QuestionActionType.SetLoopRow: {
                const block = getPropertyString(action, PropertyType.Block);
                const value = getPropertyString(action, PropertyType.Value);
                return t("debug.panels.flow.setRow", { block, value });
            }
            case QuestionActionType.MobileAudioRecording: {
                const start = getPropertyBoolean(action, PropertyType.Mode, "Start");
                return t("debug.panels.flow.audioRecording", { context: start ? "start" : "stop" });
            }
            case QuestionActionType.SendEmail:
                return t("debug.panels.flow.sendEmail");
            case QuestionActionType.SetPanelistData:
                return t("debug.panels.flow.setPanelistData");
            default:
                return null;
        }
    }

    function getActionConditionText(action: ActionModel) {
        if (!action.condition) return "";
        const evaluated = dispatch(evaluate(action.condition)).asBoolean();
        return (
            <>
                {getActionTitle(action)}
                {` ${evaluated ? "" : t("debug.panels.flow.conditionNotMet")}`}
            </>
        );
    }

    return (
        <div ref={ref} className={classes.flowQuestionContainer}>
            <div className={classes.flowQuestion}>
                <span className={classes.flowQuestionIcon}>
                    <QuestionTypeIcon type={type} />
                </span>
                <span>{name}</span>
                {showLogic &&
                    actions.map((action) => (
                        <div className={classes.flowQuestionLogic} key={action.name}>
                            <span className={classes.flowQuestionLogicIcon}>
                                <ActionTypeIcon type={action.type} />
                            </span>
                            {getActionConditionText(action)}
                        </div>
                    ))}
            </div>
            <div className={classes.flowQuestionActions}>
                <span
                    className={classNames(
                        classes.flowQuestionAction,
                        classes.flowQuestionActionButton,
                        showLogic && classes.flowQuestionActionActive,
                    )}
                    data-tip={t("debug.panels.flow.toggleLogic")}
                    onClick={() => setShowLogic((s) => !s)}
                >
                    {actions.length > 0 && (
                        <span className="fa-layers">
                            <FontAwesomeIcon icon={["fas", "circle"]} />
                            <span
                                className="fa-layers-text"
                                style={{
                                    fontSize: "0.7em",
                                    transform: "translate(-51%, -53%)",
                                    color: "white",
                                }}
                            >
                                {actions.length}
                            </span>
                        </span>
                    )}
                </span>
                {completed && (
                    <span
                        className={classes.flowQuestionAction}
                        data-tip={t("debug.panels.flow.completed")}
                    >
                        <FontAwesomeIcon icon={["far", "check-circle"]} />
                    </span>
                )}
            </div>
            {!firstOfPage && <div className={classes.questionConnector} />}
        </div>
    );
}
